'use client';

import { useState } from 'react';
import ApartmentCard from './ApartmentCard';
import ComparisonTable from './ComparisonTable';
import PricingCalculator from './PricingCalculator';
import TestimonialCard from './TestimonialCard';
import Icon from '@/components/ui/AppIcon';



interface Amenity {
  icon: string;
  label: string;
}

interface Apartment {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  size: string;
  bedrooms: string;
  bathrooms: string;
  maxOccupancy: string;
  priceFrom: string;
  amenities: Amenity[];
  features: string[];
}

interface ComparisonFeature {
  category: string;
  features: {
    name: string;
    luxos: boolean | string;
    primera?: boolean | string;
    classica: boolean | string;
  }[];
}

interface FloorPlan {
  id: string;
  name: string;
  image: string;
  alt: string;
  dimensions: {
    label: string;
    value: string;
  }[];
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  testimonial: string;
  stayDuration: string;
  apartmentType: string;
}

export default function ApartmentCategoriesInteractive() {
  const [activeTab, setActiveTab] = useState<'overview' | 'comparison' | 'pricing'>('overview');
  const [selectedApartment, setSelectedApartment] = useState<string | null>(null);

  const apartments: Apartment[] = [
  {
    id: 'luxos',
    name: 'Luxos Luxury',
    tagline: 'Executive Excellence Redefined',
    description: 'Sophisticated living spaces designed for discerning executives who demand the finest. Experience unparalleled luxury with premium furnishings, state-of-the-art amenities, and meticulous attention to every detail.',
    image: "/assets/images/IMG20251021160224_copy-1767696990093.jpg",
    alt: 'Luxurious Luxos apartment with premium furnishings, wooden flooring, elegant beige walls, and pristine white bedding',
    size: '400 sq ft',
    bedrooms: '1 BHK',
    bathrooms: '1',
    maxOccupancy: 'Up to 2 adults & 1 child (under 6 years)',
    priceFrom: '4,750',
    amenities: [
    { icon: 'WifiIcon', label: 'High-Speed WiFi' },
    { icon: 'TvIcon', label: '55" 4K Android TV' },
    { icon: 'FireIcon', label: 'Private Kitchen' },
    { icon: 'HomeIcon', label: 'Smart Home' },
    { icon: 'ShieldCheckIcon', label: '24/7 Concierge' },
    { icon: 'BoltIcon', label: 'Power Backup' }],

    features: [
    'Designer furniture and premium finishes',
    'Fully equipped private kitchen',
    'Dedicated workspace with ergonomic setup',
    'Premium bedding and linens',
    'Smart home automation',
    'Private balcony with city views']

  },
  {
    id: 'primera',
    name: 'Primera Premium',
    tagline: 'Modern Comfort, Thoughtfully Designed',
    description: 'Positioned between Luxos and Classica, Primera offers modern comfort, stylish décor, and smart use of space—perfect for guests who want more than standard amenities at an accessible price.',
    image: "/assets/images/IMG20251018151027_copy-1767697078477.jpg",
    alt: 'Modern Primera bedroom with wooden headboard featuring backlit design, pristine white bedding, ceiling fan, air conditioning, and beige walls',
    size: '400 sq ft',
    bedrooms: '1 BHK',
    bathrooms: '1',
    maxOccupancy: 'Up to 2 adults & 1 child (under 6 years)',
    priceFrom: '3,850',
    amenities: [
    { icon: 'WifiIcon', label: 'High-Speed WiFi' },
    { icon: 'TvIcon', label: '43" 4K Android TV' },
    { icon: 'FireIcon', label: 'Compact Kitchen' },
    { icon: 'HomeIcon', label: 'Modern Furnishing' },
    { icon: 'ShieldCheckIcon', label: '24/7 Security' },
    { icon: 'BoltIcon', label: 'Power Backup' }],

    features: [
    'Modern and stylish furniture',
    'Smart space utilization',
    'Compact but well-equipped kitchen',
    'Comfortable work-from-home setup',
    'Quality bedding and essentials',
    'Contemporary décor and finishes']

  },
  {
    id: 'classica',
    name: 'Classica Comfort',
    tagline: 'Timeless Comfort, Modern Convenience',
    description: 'Thoughtfully designed apartments that blend classic comfort with contemporary amenities. Perfect for professionals and families seeking quality living spaces without compromise.',
    image: "/assets/images/IMG20251029135419_1copy-1767817703345.jpg",
    alt: 'Bright Classica studio apartment featuring white walls, ceiling fan, modern furnishings, comfortable bed with floral bedding, and golden curtains',
    size: '400 sq ft',
    bedrooms: '1 BHK',
    bathrooms: '1',
    maxOccupancy: 'Up to 2 adults & 1 child (under 6 years)',
    priceFrom: '3,100',
    amenities: [
    { icon: 'WifiIcon', label: 'High-Speed WiFi' },
    { icon: 'TvIcon', label: '32" 4K Android TV' },
    { icon: 'FireIcon', label: 'Equipped Kitchen' },
    { icon: 'HomeIcon', label: 'Quality Furnishing' },
    { icon: 'ShieldCheckIcon', label: '24/7 Security' },
    { icon: 'BoltIcon', label: 'Power Backup' }],

    features: [
    'Comfortable and functional furniture',
    'Well-equipped kitchen with essentials',
    'Dedicated work area',
    'Quality bedding and linens',
    'Ample storage space',
    'Natural lighting throughout']

  }];


  const comparisonData: ComparisonFeature[] = [
  {
    category: 'Space & Layout',
    features: [
    { name: 'Apartment Size', luxos: '400 sq ft', primera: '400 sq ft', classica: '400 sq ft' },
    { name: 'Bedroom Configuration', luxos: '1 BHK', primera: '1 BHK', classica: '1 BHK' },
    { name: 'Bathroom Count', luxos: '1', primera: '1', classica: '1' },
    { name: 'Private Balcony', luxos: true, primera: true, classica: true },
    { name: 'Dedicated Workspace', luxos: true, primera: true, classica: true }]

  },
  {
    category: 'Kitchen & Dining',
    features: [
    { name: 'Kitchen Type', luxos: 'Private Modular', primera: 'Private Modular', classica: 'Standard Equipped' },
    { name: 'Premium Appliances', luxos: true, primera: true, classica: false },
    { name: 'Microwave Oven', luxos: true, primera: true, classica: true },
    { name: 'Dishwasher', luxos: false, primera: false, classica: false },
    { name: 'Dining Table', luxos: '2-seater', primera: '2-seater', classica: '2-seater' }]

  },
  {
    category: 'Technology & Entertainment',
    features: [
    { name: 'Smart TV Size', luxos: '55" 4K', primera: '43" Smart', classica: '43" Full HD' },
    { name: 'TV in Living Room', luxos: true, primera: true, classica: true },
    { name: 'TV in Bedroom', luxos: true, primera: false, classica: false },
    { name: 'WiFi Speed', luxos: '100 Mbps', primera: '100 Mbps', classica: '100 Mbps' },
    { name: 'In-Room Access Points', luxos: true, primera: true, classica: false },
    { name: 'Ethernet RJ45', luxos: true, primera: true, classica: false },
    { name: 'Streaming Services', luxos: true, primera: true, classica: true }]

  },
  {
    category: 'Comfort & Luxury',
    features: [
    { name: 'Air Conditioning', luxos: 'Bedroom', primera: 'Bedroom', classica: 'Bedroom' },
    { name: 'Premium Bedding', luxos: '10" Spring Mattress', primera: '8"Spring Mattress', classica: '6" Foam Mattress' },
    { name: 'Designer Furniture', luxos: true, primera: true, classica: false },
    { name: 'Premium Toiletries', luxos: true, primera: true, classica: false },
    { name: 'Bathtub', luxos: false, primera: false, classica: false },
    { name: 'Shower', luxos: 'Rain Shower', primera: 'Misty Shower', classica: 'Regular Shower' }]

  },
  {
    category: 'Services & Amenities',
    features: [
    { name: 'Housekeeping Frequency', luxos: 'Daily', primera: 'Daily', classica: 'Daily' },
    { name: 'Laundry Service', luxos: 'Chargeable', primera: 'Chargeable', classica: 'Chargeable' },
    { name: 'Airport Pickup', luxos: 'Chargeable', primera: 'Chargeable', classica: 'Chargeable' },
    { name: 'Concierge Service', luxos: '24/7', primera: '24/7', classica: '24/7' },
    { name: 'Welcome Amenities', luxos: true, primera: true, classica: true }]

  }];


  const floorPlans: FloorPlan[] = [
  {
    id: 'luxos-2bhk',
    name: 'Luxos 2 BHK',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13e7c08e7-1782747612066.png",
    alt: 'Detailed floor plan of Luxos 2 BHK apartment showing living room, two bedrooms, two bathrooms, kitchen, and balcony layout',
    dimensions: [
    { label: 'Total Area', value: '1200 sq ft' },
    { label: 'Living Room', value: '350 sq ft' },
    { label: 'Master Bedroom', value: '280 sq ft' },
    { label: 'Second Bedroom', value: '220 sq ft' }]

  },
  {
    id: 'luxos-3bhk',
    name: 'Luxos 3 BHK',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10807a5d8-1766216781896.png",
    alt: 'Detailed floor plan of Luxos 3 BHK apartment showing spacious living area, three bedrooms, three bathrooms, modern kitchen, and large balcony',
    dimensions: [
    { label: 'Total Area', value: '1500 sq ft' },
    { label: 'Living Room', value: '400 sq ft' },
    { label: 'Master Bedroom', value: '300 sq ft' },
    { label: 'Bedrooms 2 & 3', value: '220 sq ft each' }]

  },
  {
    id: 'primera-1bhk',
    name: 'Primera 1 BHK',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18151d7f4-1777485215609.png",
    alt: 'Detailed floor plan of Primera 1 BHK apartment showing efficient layout with living area, bedroom, bathroom, and compact kitchen',
    dimensions: [
    { label: 'Total Area', value: '400 sq ft' },
    { label: 'Living Area', value: '150 sq ft' },
    { label: 'Bedroom', value: '180 sq ft' },
    { label: 'Kitchen', value: '70 sq ft' }]

  },
  {
    id: 'classica-1bhk',
    name: 'Classica 1 BHK',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107c9c3c5-1775471399468.png",
    alt: 'Detailed floor plan of Classica 1 BHK apartment showing compact living room, bedroom, bathroom, and kitchen layout',
    dimensions: [
    { label: 'Total Area', value: '900 sq ft' },
    { label: 'Living Room', value: '280 sq ft' },
    { label: 'Bedroom', value: '250 sq ft' },
    { label: 'Kitchen', value: '120 sq ft' }]

  },
  {
    id: 'classica-2bhk',
    name: 'Classica 2 BHK',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10807a5d8-1766216781896.png",
    alt: 'Detailed floor plan of Classica 2 BHK apartment showing living room, two bedrooms, two bathrooms, and functional kitchen',
    dimensions: [
    { label: 'Total Area', value: '1100 sq ft' },
    { label: 'Living Room', value: '320 sq ft' },
    { label: 'Master Bedroom', value: '260 sq ft' },
    { label: 'Second Bedroom', value: '200 sq ft' }]

  }];


  const testimonials: Testimonial[] = [
  {
    name: 'Rajesh Kumar',
    role: 'Senior Vice President',
    company: 'Tech Solutions India',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13a6fa8d3-1763295813274.png",
    alt: 'Professional headshot of Indian businessman in navy suit with confident smile',
    rating: 5,
    testimonial: 'The Luxos Luxury apartment exceeded all expectations. The proximity to IIM Bangalore made my consulting project seamless, and the premium amenities allowed me to maintain my executive lifestyle. The smart home features and dedicated workspace were game-changers for my productivity.',
    stayDuration: '45 days',
    apartmentType: 'Luxos Luxury 2 BHK'
  },
  {
    name: 'Sarah Mitchell',
    role: 'Project Manager',
    company: 'Global Consulting Group',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1456eb2f9-1763294356174.png",
    alt: 'Professional headshot of Caucasian woman with blonde hair in business attire',
    rating: 5,
    testimonial: 'Relocating to Bangalore for six months was daunting, but Proximus made it feel like home from day one. The Classica Comfort apartment had everything I needed, and the housekeeping service kept everything pristine. The location near corporate hubs saved me hours of commute time.',
    stayDuration: '6 months',
    apartmentType: 'Classica Comfort 1 BHK'
  },
  {
    name: 'Amit Patel',
    role: 'Entrepreneur',
    company: 'StartUp Ventures',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_150c1f9be-1763300209347.png",
    alt: 'Professional headshot of Indian entrepreneur in casual business attire with warm smile',
    rating: 5,
    testimonial: 'As a startup founder, I needed flexibility and quality without breaking the bank. The Classica apartment provided exactly that. The high-speed WiFi, comfortable workspace, and professional environment helped me close deals and build my business. Highly recommend for entrepreneurs!',
    stayDuration: '3 months',
    apartmentType: 'Classica Comfort 2 BHK'
  }];


  const handleViewDetails = (id: string) => {
    setSelectedApartment(id);
  };

  const handleBookNow = (id: string) => {
    window.open('https://bookingengine.stayflexi.com/?hotel_id=33526', '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative bg-gradient-to-br from-primary via-secondary to-primary py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-6">
              Discover Your Perfect Space
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Choose from our meticulously designed apartment categories, each crafted to deliver exceptional comfort and convenience for your Bangalore stay
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-wrap gap-4 mb-12 justify-center">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
            activeTab === 'overview' ? 'bg-accent text-white shadow-elevated' : 'bg-white text-text-secondary hover:bg-muted border border-border'}`
            }>

            <Icon name="HomeIcon" size={20} />
            <span>Overview</span>
          </button>
          <button
            onClick={() => setActiveTab('comparison')}
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
            activeTab === 'comparison' ? 'bg-accent text-white shadow-elevated' : 'bg-white text-text-secondary hover:bg-muted border border-border'}`
            }>

            <Icon name="TableCellsIcon" size={20} />
            <span>Compare</span>
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
            activeTab === 'pricing' ? 'bg-accent text-white shadow-elevated' : 'bg-white text-text-secondary hover:bg-muted border border-border'}`
            }>

            <Icon name="CurrencyRupeeIcon" size={20} />
            <span>Pricing</span>
          </button>
        </div>

        {activeTab === 'overview' &&
        <div className="space-y-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {apartments.map((apartment) =>
            <ApartmentCard
              key={apartment.id}
              {...apartment}
              onViewDetails={handleViewDetails}
              onBookNow={handleBookNow} />

            )}
            </div>

            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 md:p-12">
              <div className="text-center mb-12">
                <h2 className="font-display text-4xl font-bold text-primary mb-4">
                  What Our Guests Say
                </h2>
                <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                  Real experiences from business travelers and professionals who chose Proximus
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) =>
              <TestimonialCard key={index} {...testimonial} />
              )}
              </div>
            </div>
          </div>
        }

        {activeTab === 'comparison' &&
        <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl font-bold text-primary mb-4">
                Detailed Feature Comparison
              </h2>
              <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                Compare every aspect of our apartment categories to make an informed decision
              </p>
            </div>
            <ComparisonTable comparisonData={comparisonData} />
          </div>
        }

        {activeTab === 'pricing' &&
        <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl font-bold text-primary mb-4">
                Transparent Pricing Calculator
              </h2>
              <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                Calculate your stay cost with our flexible pricing tiers and duration discounts
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <PricingCalculator basePrice={4750} apartmentName="Luxos" />
              <PricingCalculator basePrice={3850} apartmentName="Primera" />
              <PricingCalculator basePrice={3100} apartmentName="Classica" />
            </div>
          </div>
        }
      </div>

      <div className="bg-gradient-to-r from-primary to-secondary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-display text-4xl font-bold text-white mb-6">
              Ready to Experience Proximus?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Book your ideal apartment today and discover why discerning travelers choose Proximus for their Bangalore stays
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => window.open('https://bookingengine.stayflexi.com/?hotel_id=33526', '_blank')}
                className="px-8 py-4 bg-accent text-white font-semibold rounded-lg hover:bg-accent/90 shadow-elevated hover:shadow-premium transition-all duration-300 flex items-center space-x-2">

                <Icon name="CalendarIcon" size={20} />
                <span>Check Availability</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>);
}